import { useEffect, useMemo } from 'react';
import { Photo } from '../types';
import { useAvatarStore } from '../stores/useAvatarStore';

export interface UseStableRandomImageOptions {
  /**
   * Milliseconds. Overrides the shared avatar-store rotation period (clamped to
   * 1 h–30 d) for every consumer; absent keeps the store value.
   */
  rotationPeriod?: number;
  
  /**
   * Whether to enable the stable randomization.
   * If false, falls back to immediate random selection.
   */
  enabled?: boolean;
  
  /**
   * Unique identifier for this image selection.
   * Used to distinguish between different usage contexts.
   */
  selectionId?: string;
}

const NO_PHOTOS: Photo[] = [];

/** Picks a stable image URL per event key for the current rotation period; returns defaultImage when no image photo exists. */
export const useStableRandomImage = (
  photos: Photo[],
  defaultImage: string,
  eventKey: string,
  options: UseStableRandomImageOptions = {}
): string => {
  const {
    rotationPeriod,
    enabled = true,
    selectionId = 'default'
  } = options;

  const avatarStore = useAvatarStore();

  const uniqueEventKey = useMemo(() => {
    return selectionId === 'default' ? eventKey : `${eventKey}-${selectionId}`;
  }, [eventKey, selectionId]);

  useEffect(() => {
    if (rotationPeriod && rotationPeriod !== avatarStore.rotationPeriod) {
      avatarStore.setRotationPeriod(rotationPeriod);
    }
  }, [rotationPeriod, avatarStore]);

  // Callers outside the type system may pass null; treat it as no photos.
  const availablePhotos = photos ?? NO_PHOTOS;

  // Compute selected image synchronously to prevent flickering
  const selectedImage = useMemo(() => {
    if (!enabled) {
      const imagePhotos = availablePhotos.filter(photo => photo.type === 'image');
      if (imagePhotos.length === 0) {
        return defaultImage;
      }

      const randomIndex = Math.floor(Math.random() * imagePhotos.length);
      return imagePhotos[randomIndex].url;
    }

    return avatarStore.selectOrCreateAvatarSelection(
      uniqueEventKey,
      availablePhotos,
      defaultImage
    );
  }, [availablePhotos, defaultImage, uniqueEventKey, enabled, avatarStore]);

  return selectedImage;
};
